import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { getErrorMessage } from '@/utils/errorMessage';
import { DocumentService } from '@/services/documentService';
import {
  createUploadRow, InvestorUploadBatch, validateBatchLimits, validateUploadFields,
  type InvestorUploadRow, type UploadChannel, type UploadFields
} from '@/services/investorUploadBatch';
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning';
import { DocumentUploadFields } from './DocumentUploadFields';

const CHANNEL_LABEL: Record<UploadChannel, string> = { wholesale: 'Wholesale', correspondent: 'Correspondent' };

export interface BatchDocumentUploadProps {
  investorId: string;
  organizationId: string;
  availableCategories: string[];
  channels: UploadChannel[];
  defaultChannel: UploadFields['channel'];
  onClose: () => void;
  onUploadComplete: () => void;
  onPendingChange?: (pending: boolean) => void;
}

export function BatchDocumentUploadModal(props: BatchDocumentUploadProps) {
  const { onPendingChange } = props;
  const [rows, setRows] = useState<InvestorUploadRow[]>([]);
  const rowsRef = useRef(rows);
  const returnFocus = useRef(document.activeElement as HTMLElement | null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const session = useRef<InvestorUploadBatch | null>(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const pending = busy || rows.some(row => !['complete', 'skipped'].includes(row.status));
  useBeforeUnloadWarning(pending);
  useEffect(() => {
    onPendingChange?.(pending);
    return () => onPendingChange?.(false);
  }, [pending, onPendingChange]);
  // Leaving stops scheduling; files already uploading may still finish and save once.
  useEffect(() => () => session.current?.stop(), []);

  const publish = useCallback(() => setRows([...rowsRef.current]), []);
  const addFiles = useCallback((files: File[]) => {
    if (busyRef.current || rowsRef.current.some(row => row.checkpoint.metadata)) {
      return;
    }
    const next = [...rowsRef.current, ...files.map(file => createUploadRow(file, props.defaultChannel))];
    const limit = validateBatchLimits(next.map(row => row.file));
    if (limit) {
      setError(limit); return;
    }
    rowsRef.current = next;
    setError('');
    publish();
  }, [props.defaultChannel, publish]);
  const dropzone = useDropzone({
    onDrop: addFiles, multiple: true, noClick: true, noKeyboard: true,
    disabled: busy || rows.some(row => row.checkpoint.metadata)
  });

  const updateFields = (row: InvestorUploadRow, fields: UploadFields) => {
    const identityChanged = fields.category !== row.fields.category || fields.channel !== row.fields.channel ||
      fields.subType !== row.fields.subType;
    row.fields = fields;
    row.error = undefined;
    if (!row.checkpoint.metadata) {
      row.checked = false;
      row.status = 'draft';
      if (identityChanged) {
        row.duplicateChoice = undefined; row.duplicate = undefined;
      }
    }
    // Any edit can change a sibling's within-batch duplicate result.
    for (const sibling of rowsRef.current) {
      if (!sibling.checkpoint.metadata && sibling.status !== 'skipped') {
        sibling.checked = false;
      }
    }
    publish();
  };
  const run = async (prepare: boolean) => {
    if (busyRef.current) {
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError('');
    session.current = new InvestorUploadBatch(props.investorId, props.organizationId, rowsRef.current, publish);
    try {
      if (prepare) {
        await session.current.prepare(props.availableCategories, props.channels);
      } else {
        await session.current.run(props.availableCategories, props.channels);
        props.onUploadComplete();
      }
    } catch (failure) {
      setError(getErrorMessage(failure, 'Upload failed. Please retry.'));
    } finally {
      busyRef.current = false;
      setBusy(false);
      publish();
    }
  };
  const requestClose = () => {
    if (busyRef.current) {
      setError('Use Stop remaining and wait for active uploads to finish before closing.'); return;
    }
    if (pending) {
      setConfirmClose(true); return;
    }
    props.onClose();
  };
  const ready = rows.some(row => !['complete', 'skipped'].includes(row.status)) &&
    rows.every(row => ['complete', 'skipped'].includes(row.status) || (
      row.checked && !validateUploadFields(row.fields, props.availableCategories, props.channels) &&
      (!row.duplicate || row.duplicateChoice === 'separate')
    ));
  const started = rows.some(row => row.checkpoint.metadata);
  // Bulk controls show the value every selected file shares (blank when they differ),
  // so a choice stays visible and arrow keys can reach every option.
  const selectedRows = rows.filter(row => selected.includes(row.id));
  const sharedValue = (pick: (row: InvestorUploadRow) => string) =>
    selectedRows.length && selectedRows.every(row => pick(row) === pick(selectedRows[0])) ? pick(selectedRows[0]) : '';
  const applyToSelected = (patch: (fields: UploadFields) => UploadFields) =>
    rowsRef.current.filter(row => selected.includes(row.id)).forEach(row => updateFields(row, patch(row.fields)));
  const counts = {
    complete: rows.filter(row => Boolean(row.checkpoint.document)).length,
    failed: rows.filter(row => row.status === 'failed').length,
    skipped: rows.filter(row => row.status === 'skipped').length
  };
  return <Dialog open onOpenChange={open => {
    if (!open) {
      requestClose();
    }
  }}>
    <DialogContent className="max-h-[90dvh] max-w-4xl overflow-y-auto p-4 sm:p-6"
      onInteractOutside={event => event.preventDefault()}
      onCloseAutoFocus={event => {
        event.preventDefault();
        returnFocus.current?.focus();
      }}>
      <DialogTitle>Upload documents</DialogTitle>
      <DialogDescription>Upload up to 25 files for this investor. Maximum 20 MB per file and 200 MB total. PDFs process automatically after saving.</DialogDescription>
      <div {...dropzone.getRootProps()} className="min-w-0 space-y-2 rounded-md border border-dashed border-input p-4">
        <input {...dropzone.getInputProps({ 'aria-label': 'Select documents', accept: DocumentService.FILE_INPUT_ACCEPT })} />
        <p className="text-sm">Drop documents here or choose files.</p>
        <p className="text-xs text-muted-foreground">{DocumentService.ACCEPTED_FORMATS_LABEL}</p>
        <Button type="button" variant="outline" disabled={busy || started} onClick={dropzone.open}>Choose files</Button>
      </div>
      {error && <p role="alert" className="break-words text-sm text-destructive">{error}</p>}
      {!!rows.length && <div className="flex min-w-0 flex-wrap items-center gap-3 rounded-md bg-muted p-3">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={busy || started}
          checked={selected.length === rows.length} onChange={event => setSelected(event.target.checked ? rows.map(row => row.id) : [])} />Select all</label>
        <label className="min-w-0 text-sm">Apply type to selected
          <select aria-label="Apply type to selected" value={sharedValue(row => row.fields.category)}
            disabled={busy || started || !selected.length}
            className="ml-2 h-10 max-w-full rounded-md border border-input bg-background px-2"
            onChange={event => {
              const category = event.target.value;
              if (category) {
                applyToSelected(fields => ({ ...fields, category, subType: '', effectiveDate: '' }));
              }
            }}>
            <option value="">Choose type</option>
            {props.availableCategories.map(category => <option key={category}>{category}</option>)}
          </select>
        </label>
        {props.channels.length > 1 && <label className="min-w-0 text-sm">Apply channel to selected
          <select aria-label="Apply channel to selected" value={sharedValue(row => row.fields.channel)}
            disabled={busy || started || !selected.length}
            className="ml-2 h-10 max-w-full rounded-md border border-input bg-background px-2"
            onChange={event => {
              const channel = event.target.value as UploadChannel | '';
              if (channel) {
                applyToSelected(fields => ({ ...fields, channel }));
              }
            }}>
            <option value="">Choose channel</option>
            {props.channels.map(channel => <option key={channel} value={channel}>{CHANNEL_LABEL[channel]}</option>)}
          </select>
        </label>}
      </div>}
      <div className="min-w-0 space-y-3">
        {rows.map((row, index) => <section key={row.id} aria-label={`File ${index + 1}: ${row.file.name}`}
          className="min-w-0 space-y-3 rounded-md border border-border p-3">
          <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
            <label className="flex min-w-0 flex-1 items-start gap-2 text-sm font-medium">
              <input type="checkbox" disabled={busy || started} checked={selected.includes(row.id)}
                onChange={event => setSelected(previous => event.target.checked ? [...previous, row.id] : previous.filter(id => id !== row.id))} />
              <span className="min-w-0 break-all">{row.file.name}</span>
            </label>
            <span className="text-xs text-muted-foreground">{(row.file.size / 1024 / 1024).toFixed(2)} MB</span>
            {!started && <Button variant="ghost" size="sm" disabled={busy} aria-label={`Remove ${row.file.name}`} onClick={() => {
              rowsRef.current = rowsRef.current.filter(item => item.id !== row.id);
              setSelected(previous => previous.filter(id => id !== row.id));
              rowsRef.current.forEach(item => {
                item.checked = false;
              });
              publish();
            }}>Remove</Button>}
          </div>
          <p className="text-sm text-muted-foreground">{row.fields.category || 'Type required'} · {row.fields.channel ? CHANNEL_LABEL[row.fields.channel] : 'Channel required'}</p>
          <details open={row.status === 'draft'}>
            <summary className="cursor-pointer text-sm font-medium focus-visible:outline focus-visible:outline-2">Document details</summary>
            <div className="pt-3"><DocumentUploadFields value={row.fields} onChange={fields => updateFields(row, fields)}
              categories={props.availableCategories} channels={props.channels}
              disabled={busy || row.status === 'complete' || row.status === 'skipped'} lockIdentity={Boolean(row.checkpoint.metadata)} /></div>
          </details>
          {row.duplicate && !row.checkpoint.metadata && <div className="space-y-2 text-sm">
            <p className="break-words">Duplicate detected: {row.duplicate}</p>
            <Button variant="outline" disabled={busy} onClick={() => {
              row.duplicateChoice = 'skip'; row.status = 'skipped'; publish();
            }}>Skip</Button>{' '}
            <Button variant="outline" disabled={busy} onClick={() => {
              row.duplicateChoice = 'separate'; row.status = 'ready'; publish();
            }}>Upload as separate document</Button>
          </div>}
          <p role="status" className="text-sm">{row.status === 'uploading' ? `Uploading ${row.progress}%` :
            row.status === 'saving' ? 'Saving document' :
              row.status === 'complete' ? 'Saved' : row.status === 'duplicate' ? 'Choose how to handle this duplicate' : row.status}</p>
          {row.status === 'uploading' && <progress aria-label={`Upload progress for ${row.file.name}`} className="w-full" value={row.progress} max={100} />}
          {(row.error || (!row.checkpoint.metadata && DocumentService.validateFile(row.file))) &&
            <p role="alert" className="break-words text-sm text-destructive">{row.error || DocumentService.validateFile(row.file)}</p>}
        </section>)}
      </div>
      <p role="status" className="text-sm">{counts.complete} saved · {counts.failed} need attention · {counts.skipped} skipped</p>
      {pending && <p className="text-sm text-muted-foreground">
        Keep this page open until uploading finishes. Leaving may interrupt unfinished uploads, and you’ll need to select those files again. Documents already saved will remain saved.
      </p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={requestClose} disabled={busy}>Close</Button>
        {busy ? <Button variant="outline" onClick={() => {
          session.current?.stop(); setError('Stopping: active uploads will finish. Remaining files will wait.');
        }}>Stop remaining</Button> : <>
          {rows.some(row => !row.checkpoint.metadata && row.status !== 'skipped') && <Button variant="outline" disabled={!rows.length} onClick={() => void run(true)}>Review files</Button>}
          <Button disabled={!ready} onClick={() => void run(false)}>{started ? 'Retry remaining' : 'Upload documents'}</Button>
        </>}
      </div>
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogTitle>Close this batch?</AlertDialogTitle>
          <AlertDialogDescription>
            Saved documents stay saved. Unfinished uploads will lose their retry state when you close.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep working</AlertDialogCancel>
            <AlertDialogAction onClick={props.onClose}>Close batch</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DialogContent>
  </Dialog>;
}
